/**
 * App Shell sitemap — TYPES + shell-owned system fallback (Spec №11, D-M4).
 *
 * The authoritative, module-merged sitemap now comes from the server verb
 * `get_app_sitemap` (see engine/app-sitemap.ts) and is consumed via
 * `useAppSitemap()`. This file no longer hardcodes the Places data — it keeps
 * the shared types and a minimal system-only fallback rendered until the verb
 * resolves. The `staff` axis is still composed at render time from the roster
 * (AppShellLayout); it is not part of the assembled sitemap.
 */

export type SitemapAxis = "places" | "staff";

/** Which live count drives a nav badge (extended as more sources land). */
export type BadgeSource = "needs_you" | null;

/** Named glyph rendered by the rail. Open string (Tabler / named glyphs, D-M7);
 *  known names have SVG paths in the rail, unknown names fall back. */
export type SitemapGlyph = string;

export interface SitemapEntry {
  axis: SitemapAxis;
  label: string;
  /** Absolute route under the shell (route / view targets). */
  route?: string;
  /** Workbench name resolved by the client-side workbench registry. */
  workbench?: string;
  /** Declared-view reference (Stage 3 renderer). */
  view?: { entity_type: string; view_id?: string };
  /** Live count that decorates this entry with a badge. */
  badgeSource?: BadgeSource;
  glyph?: SitemapGlyph;
  /** Sort order within the axis. */
  order?: number;
  /** Provenance — the module that contributed this entry. */
  module?: string;
  /**
   * №53 (53.0, D-CR12 / D-CR20) — the server's answer to "is this a rail row?".
   *
   * `"day"` = one of the rail's six. Absent = assembled and reachable (⌘K, deep
   * links) but off the rail. Mirrors `AppSitemapEntry.section` in
   * `src/engine/app-sitemap.ts`, which carries the full note.
   */
  section?: "day";
}

/**
 * Shell-owned fallback rendered until `get_app_sitemap` resolves — the system
 * Needs You home only. The full, module-merged sitemap (Trust, greyed roadmap
 * Places, any installed module's Places) arrives from the verb.
 */
export const SYSTEM_FALLBACK_PLACES: SitemapEntry[] = [
  // №53 (53.0) — `section: "day"` is NOT optional here, and leaving it off was a
  // real defect for the ten minutes it existed. The rail renders `section === "day"`
  // and nothing else, so a fallback without the mark degrades to an EMPTY RAIL —
  // in exactly the case this constant exists to cover, a sitemap call that failed.
  // The user would lose the shell's navigation at the moment the workspace is
  // already misbehaving. Caught by rendering the frame rather than by a gate.
  { axis: "places", label: "Needs you", route: "/app", badgeSource: "needs_you", glyph: "needs-you", order: 0, section: "day" },
  // №53 (53.2) — Workforce joins the day section the moment its surface exists.
  // D-NAV3 is why it was not declared in 53.0 alongside Needs you: a rail row
  // pointing at an unbuilt Place is exactly what the dead-nav ban forbids, and
  // E-25 ruled that a one-row rail was correct rather than embarrassing. The rail
  // grows a row per rung, and this is the second.
  { axis: "places", label: "Workforce", route: "/app/workforce", glyph: "users", order: 1, section: "day" },
  // №53 (53.4) — Handled joins the day section. Third row, same rule as the second:
  // D-NAV3 held it back until the surface existed, and E-25 ruled that growing the
  // rail a row per rung is correct rather than embarrassing.
  { axis: "places", label: "Handled", route: "/app/handled", glyph: "check", order: 3, section: "day" },
];
